import React, { useState, useEffect } from 'react';
import Modal from './Modal';

// Matches the server's limit in pca-ui/src/validations/mark_non_sales.py.
const MAX_REASON_LENGTH = 500;

// Marks an unsuccessful call non-sales so it is left out of scores. The reason is optional.
const NonSalesModal = ({ open, onClose, onSave }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset fields whenever the modal opens
  useEffect(() => {
    if (open) {
      setReason('');
      setError('');
    }
  }, [open]);

  const trimmed = reason.trim();
  const tooLong = trimmed.length > MAX_REASON_LENGTH;

  const handleSubmit = async () => {
    if (tooLong) return;
    setSaving(true);
    setError('');
    try {
      await onSave(trimmed);
    } catch (err) {
      setError(err?.message || 'Failed to mark the call as non-sales. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Mark as Non-sales">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Use this for an unsuccessful call that should not be evaluated. It stays viewable, but is
          marked non-sales so it can be left out of scores. You can mark it as sales again later.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            rows={4}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Customer declined the offer; agent did not flag the call as unsuccessful"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          />
          <p className={`text-xs mt-1 ${tooLong ? 'text-red-600' : 'text-gray-400'}`}>
            {trimmed.length} / {MAX_REASON_LENGTH}
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || tooLong}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Mark as Non-sales'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NonSalesModal;
